import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {BaseStoreItem} from '../../../../shared/store/BaseStoreItem'
import {ColonistMembershipType, LobbyImage} from '@colonist/types'
import {UIAPIEndpoints} from '../network/UIAPIEndpoints'
import {createDivIn, createHeaderIn, createImgIn, createLinkIn, setElementInnerHTML, setElementOnClick} from '../ui_utils/DomUtils'
import {UIProfileOverviewController} from './UIProfileOverviewController'
import {UIProfileGameHistoryController} from './UIProfileGameHistoryController'
import {UIProfileRankedController} from './UIProfileRankedController'
import {UIProfileRankedSeasonDropdownController} from './UIProfileRankedSeasonDropdownController'
import {UIProfileLinkController} from './UIProfileLinkController'
import {ColonistMembershipProduct} from '../../../../shared/store/ColonistMembershipProduct'
import {UIProfileFriendsController} from './UIProfileFriendsController'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {getStoreCategoryString} from '../store/StoreUtils'
import {uiManagerEvents} from '../EntryShared'

export class UIPopupProfileController {
    private readonly username: string
    private readonly profileLinkController: UIProfileLinkController
    private readonly stickyPlayerInfo: AjaxDataTypes.PublicProfileDataStickyPlayerInfo
    private readonly overviewController: UIProfileOverviewController
    private readonly rankedController: UIProfileRankedController
    private readonly gameHistoryController: UIProfileGameHistoryController
    private readonly friendsController: UIProfileFriendsController

    constructor(username: string, stickyPlayerInfo: AjaxDataTypes.PublicProfileDataStickyPlayerInfo, selectedTab: number) {
        this.username = username
        this.stickyPlayerInfo = stickyPlayerInfo

        this.profileLinkController = new UIProfileLinkController(true, username)
        this.overviewController = new UIProfileOverviewController(true)
        this.rankedController = new UIProfileRankedController(this.username, true)
        this.gameHistoryController = new UIProfileGameHistoryController(true)

        const stickyPlayerTextContainer = document.getElementById('profile_popup_sticky_player_info_text_container') as HTMLDivElement
        this.friendsController = new UIProfileFriendsController(this.username, this.profileLinkController, stickyPlayerTextContainer, true)

        this.init(selectedTab)
    }

    private init(selectedTab: number) {
        this.setOnClickHandlers()

        this.profileLinkController.showSection(selectedTab)

        void this.setStickyPlayerInfo()
        void this.makeApiCall()
    }

    private setOnClickHandlers() {
        this.profileLinkController.sectionLinks.forEach((link, idx) => {
            link.hasMadeAPICall = false
            setElementOnClick(link, () => {
                this.profileLinkController.activeLink = link
                this.profileLinkController.showSection(idx)
                void this.makeApiCall()
            }, true)
            if(UIUtilsEjsVariables.isUserOnMobile) link.classList.add('no-hover')
        })
    }

    private async setStickyPlayerInfo() {
        UIPopupProfileController.setUserIcon(this.stickyPlayerInfo.iconImageId)
        UIPopupProfileController.setMembershipIcon(this.stickyPlayerInfo.currentMembershipType)
        UIPopupProfileController.setStickyUsername(this.stickyPlayerInfo.username)
    }

    private async makeApiCall() {
        switch(this.profileLinkController.activeLink.id) {
            case this.profileLinkController.overviewSectionLink.id:
                await UIProfileLinkController.genericApiCallWrapper<AjaxDataTypes.PublicProfileDataOverview>(this.profileLinkController.activeLink,
                    UIAPIEndpoints.apiUserProfile.overview(this.username),
                    (data: AjaxDataTypes.PublicProfileDataOverview) => {
                        this.overviewController.setStats(data)
                    })
                break
            case this.profileLinkController.historySectionLink.id:
                await UIProfileLinkController.genericApiCallWrapper<AjaxDataTypes.ProfileHistoryData>(this.profileLinkController.activeLink,
                    UIAPIEndpoints.apiUserProfile.history(this.username),
                    (data: AjaxDataTypes.ProfileHistoryData) => {
                        this.setHistory(data)
                    })
                break
            case this.profileLinkController.itemsSectionLink.id:
                await UIProfileLinkController.genericApiCallWrapper<BaseStoreItem[]>(this.profileLinkController.activeLink,
                    UIAPIEndpoints.apiUserProfile.items(this.username),
                    (items: BaseStoreItem[]) => {
                        UIPopupProfileController.setItems(items)
                    })
                break
            case this.profileLinkController.rankedSectionLink.id:
                await UIProfileLinkController.genericApiCallWrapper<AjaxDataTypes.ProfileDataRankedSeasonDropdown[]>(this.profileLinkController.activeLink,
                    UIAPIEndpoints.apiUserProfileRankedSeasons(),
                    (seasons: AjaxDataTypes.ProfileDataRankedSeasonDropdown[]) => {
                        this.setRankedSeasons(seasons)
                    })
                break
            default:
                break
        }
    }

    private static setStickyUsername(username: string): void {
        const usernameElement = document.getElementById('profile_popup_sticky_player_info_username') as HTMLHeadingElement
        setElementInnerHTML(usernameElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: username}})
    }

    private static setUserIcon(iconImage: LobbyImage | undefined): void {
        const iconContainer = document.getElementById('profile_popup_sticky_player_info_icon') as HTMLDivElement
        const lobbyImage: LobbyImage = iconImage ?? LobbyImage.Guest
        createImgIn(iconContainer, lobbyImage, 'profile-icon')
    }

    private static setMembershipIcon(membershipType: ColonistMembershipType | undefined): void {
        const iconContainer = document.getElementById('profile_popup_sticky_player_info_icon') as HTMLDivElement

        if(membershipType == undefined) return

        const membershipIconLobbyImage = ColonistMembershipProduct.getMembershipProduct(membershipType).lobbyImage
        const membershipIcon = createImgIn(iconContainer, membershipIconLobbyImage, 'profile-membership-icon')
        membershipIcon.style.display = 'block'
        membershipIcon.id = 'profile_popup_sticky_player_membership_icon'
    }

    private setHistory(historyData: AjaxDataTypes.ProfileHistoryData): void {
        const {gameDatas, profileUserId} = historyData
        this.gameHistoryController.update(gameDatas, this.username, profileUserId)
    }

    private setRankedSeasons(seasons: AjaxDataTypes.ProfileDataRankedSeasonDropdown[]): void {
        new UIProfileRankedSeasonDropdownController(seasons, this.rankedController, true)
    }

    private static setItems(items: BaseStoreItem[]): void {
        const itemsDiv = document.getElementById('profile_popup_profile_items_div') as HTMLDivElement

        setElementInnerHTML(itemsDiv, {key: 'strings:colonistCommon.utils.emptyString'})

        const isInGameOnMobile = UIUtilsEjsVariables.isUserOnMobileAppOrWeb && uiManagerEvents.isSceneGame()

        for(const item of items) {
            const link = UIUtilsEjsVariables.isUserOnDiscord ? 'https://colonist.io/store' : '/store'
            const storeCategory = getStoreCategoryString(item.category)

            const container = isInGameOnMobile
                ? createDivIn(itemsDiv, 'profile-item-container')
                : createLinkIn(itemsDiv, `${link}#${storeCategory}`, {key: 'strings:colonistCommon.utils.emptyString'}, '', '', true)

            createImgIn(container, item.image)
            createHeaderIn(container, item.name, '', '', 'H4')
        }
    }
}
